<template>
  <v-card class="security-gaps-widget pa-0">
    <template v-if="widgetDataValue.loading"></template>

    <template v-else-if="widgetDataValue.data">
      <v-card-title class="security-gaps-widget__title">
        <span class="subtitle1">
          {{ $t(`dashboard.securityCoverageWidgets.${widgetType}.title`) }}</span
        >
        <v-spacer></v-spacer>

        <a class="body2 coro-link text-decoration-none" @click="navigateToGapsPage()">
          <div class="view-all-btn body2 coro-link item-clickable d-flex align-center">
            <span>{{ $t("general.viewAll") }}</span>
            <v-icon icon="$chevronRight" color="link" size="16" />
          </div>
        </a>
      </v-card-title>

      <v-card-text class="py-6 px-0">
        <div class="d-flex align-start mb-4 px-6">
          <div class="total-count">
            {{ widgetDataValue.data.total }}
          </div>
          <div class="d-flex flex-column ml-4">
            <div class="subtitle2 mb-1">
              {{ $t(`dashboard.securityCoverageWidgets.${widgetType}.misconfigurations`) }}
            </div>
            <div
              v-if="widgetDataValue.data.delta !== 0"
              class="d-flex align-center body3"
              :class="changeDirectionClass"
            >
              <span v-html="changeArrow"></span>
              <span>{{ Math.abs(widgetDataValue.data.delta) }}% vs last period</span>
            </div>
          </div>
        </div>

        <div>
          <v-list class="pa-0" density="compact">
            <v-list-item
              v-for="item in breakdownItems"
              :key="item.label"
              class="px-6 py-2 item-clickable"
              @click="navigateToGapsPage(item)"
            >
              <v-list-item-title class="body2">
                <div class="text-no-wrap d-flex align-center">
                  <div
                    class="circle mr-2"
                    :class="item.muted ? 'bg-indigo-medium' : 'bg-red-base'"
                  />
                  <div class="body2">{{ item.label }}</div>
                </div>
              </v-list-item-title>
              <template #append>
                <div :class="item.muted ? 'body2 text-indigo-medium' : 'subtitle2'">
                  {{ item.count }}
                </div>
              </template>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>
    </template>
    <widget-error-state
      v-else
      :widget-type="widgetType"
      :title="$t(`dashboard.securityCoverageWidgets.${widgetType}.error`)"
      @retry-click="$emit('fetch-data')"
    />
  </v-card>
</template>

<script lang="ts">
import { computed, type ComputedRef, defineComponent, type PropType } from "vue";
import type { SecurityGapsWidgetData } from "@/_store/dashboard/dashboard.module.ts";
import { useI18n } from "vue-i18n";
import { SecurityCoverageWidgetType } from "@/constants/dashboard.ts";
import type { DataValue } from "@/types.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes.ts";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { SecurityGapStatus } from "@/constants/security-gaps.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

type BreakdownListItem = {
  muted: boolean;
  label: string;
  count?: number;
};

export default defineComponent({
  components: { WidgetErrorState },
  props: {
    widgetDataValue: {
      type: Object as PropType<DataValue<SecurityGapsWidgetData>>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const router = useRouter();
    const filtersStore = useFiltersStore();
    const widgetType = SecurityCoverageWidgetType.SECURITY_GAPS_WIDGET;
    const data = computed(() => props.widgetDataValue.data);
    const changeDirectionClass = computed(() => {
      // Handle the null case
      if (!data.value) {
        return "text-success"; // Default class
      }
      return data.value.delta > 0 ? "text-error" : "text-success";
    });

    /**
     * Determines the arrow character based on the delta.
     * Positive delta shows an up arrow, negative shows a down arrow.
     */
    const changeArrow = computed(() => {
      // Handle the null case
      if (!data.value) {
        return "&#8595;"; // Default arrow down
      }
      return data.value.delta > 0 ? "&#8593;" : "&#8595;";
    });

    const breakdownItems: ComputedRef<BreakdownListItem[]> = computed(() => [
      {
        label: i18n.t(`dashboard.securityCoverageWidgets.${widgetType}.active`),
        count: data.value?.active,
        muted: false,
      },
      {
        label: i18n.t(`dashboard.securityCoverageWidgets.${widgetType}.ignored`),
        count: data.value?.ignored,
        muted: true,
      },
    ]);

    const navigateToGapsPage = (item?: BreakdownListItem) => {
      let gtmItem = "View All";
      if (item) {
        const status = item.muted ? SecurityGapStatus.IGNORED : SecurityGapStatus.ACTIVE;
        filtersStore.setFilters(FilterContext.SECURITY_GAPS, {
          status,
        });
        gtmItem = startCase(status);
      }
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${GTMParams.SECURITY_GAPS} - ${gtmItem}`
      );
      router.push({
        name: RouteName.SECURITY_GAPS_PAGE,
      });
    };

    return {
      changeDirectionClass,
      changeArrow,
      breakdownItems,
      widgetType,
      navigateToGapsPage,
    };
  },
});
</script>

<style scoped lang="scss">
.security-gaps-widget {
  .view-all-btn {
    text-decoration: none !important;
    user-select: none;

    &:hover span {
      text-decoration: underline !important;
    }
  }

  &__title {
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgb(var(--v-theme-indigo-faint));
    padding: 12px 24px;
  }

  .total-count {
    font-family: Quicksand;
    font-size: 40px;
    font-style: normal;
    font-weight: 500;
    line-height: 105%; /* 42px */
    letter-spacing: -0.6px;
  }
}
</style>
